import React, { useEffect, useState } from "react";
import TextEditor from "../../textEditor/TextEditor";
import InterimTemplateResponse from "../../../../interfaces/task/InterimTemplateResponse";
import { useAuth } from "../../../../firebase/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import NewInterimTemplate from "../../../../interfaces/task/NewInterimTemplate";
import { getTemplateFiles } from "../../../../services/fileServices/FileService";
import { getInterimTemplates, updateInterimTemplate } from "../../../../services/taskServices/TaskService";
import {
  createInterimTaskRelationship,
  deleteInterimTaskRelationship,
  getInterimTaskRelationships,
  getTaskRelationshipTypes,
} from "../../../../services/TaskRelationships/TaskRelationshipService";
import {
  CreateRelationshipDto,
  TaskRelationshipType,
  TemplateRelationshipResponse,
} from "../../../../interfaces/taskRelationship/taskRelationship";
import Select, { ActionMeta, MultiValue } from "react-select";
import TemplateFile from "../../../../interfaces/task/TaskFiles/TemplateFile";
import { getAllTags } from "../../../../services/tagServices/TagService"; // <-- your tag service
import Tag from "../../../../interfaces/tag/Tag"; // <-- your Tag interface
import { Button } from "react-bootstrap";
import backarrowbtn from '../../../../assets/images/backarrowbtn.svg';

interface EditTemplateInterimTaskProps {
  template: InterimTemplateResponse;
  setSelectedTemplate: (value: InterimTemplateResponse | undefined) => void;
}
interface OptionType {
  value: string | number;
  label: string;
}

const EditTemplateInterimTask: React.FC<EditTemplateInterimTaskProps> = ({
  template,
  setSelectedTemplate,
}) => {
  const { currentUser, currentRoles } = useAuth();
  const navigate = useNavigate();

  // State for the "required files"
  const [formattedFileTemplates, setFormattedFileTemplates] = useState<OptionType[]>([]);
  const [fileTemplates, setFileTemplates] = useState<TemplateFile[] | null>(null);

  // State for tags
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [formattedTags, setFormattedTags] = useState<OptionType[]>([]);

  // State for relationships
  const [existingRelationships, setExistingRelationships] = useState<TemplateRelationshipResponse[]>([]);
  const [initialRelationships, setInitialRelationships] = useState<TemplateRelationshipResponse[]>([]);
  const [relationshipTypes, setRelationshipTypes] = useState<TaskRelationshipType[]>([]);
  const [allInterimTemplates, setAllInterimTemplates] = useState<InterimTemplateResponse[]>([]);
  const [newRelationshipTargetId, setNewRelationshipTargetId] = useState<number | null>(null);
  const [newRelationshipTypeId, setNewRelationshipTypeId] = useState<number | null>(null);
  const [isAddingRelationship, setIsAddingRelationship] = useState(false);

  const [newTemplate, setNewTemplate] = useState<NewInterimTemplate>({
    id: template.id,
    title: template.title,
    description: template.description,
    information: template.information,
    requiredFiles: template.requiredFiles.map((file) => file.id.toString()),
    assignee: undefined,
    coAssignee: undefined,
    participant: undefined,
    daysFromCreationDueDate: template.daysFromCreationDueDate
      ? template.daysFromCreationDueDate.toString()
      : "0",
    taskOrganization: "1",
    taskType: "1",
    taskSubType: "4",
    dueDate: "",
    workflowGroup: "Standard",

    // We can pre-populate existing tags from 'template' if it has them
    tags: template.tags?.map(obj => obj.id), // Or from template if you have that data
    relationships: [],
  });

  // 1) Fetch the template files
  useEffect(() => {
    if (currentUser) {
      const fetchTemplateFiles = async () => {
        try {
          const authToken: string = await currentUser.getIdToken();
          const fetchedTemplateFiles = await getTemplateFiles(authToken);
          setFileTemplates(fetchedTemplateFiles);
        } catch (err) {
          console.error("Error fetching template files:", err);
        }
      };
      fetchTemplateFiles();
    }
  }, [currentUser]);

  // 2) Format the template files for react-select
  useEffect(() => {
    if (fileTemplates) {
      const newOptions = fileTemplates.map((file) => ({
        value: file.id.toString(),
        label: file.fileName,
      }));
      setFormattedFileTemplates(newOptions);
    }
  }, [fileTemplates]);

  // 3) Fetch the tags
  useEffect(() => {
    if (currentUser) {
      const fetchAllTags = async () => {
        try {
          const authToken = await currentUser.getIdToken();
          const tags = await getAllTags(authToken);
          if (tags) {
            setAllTags(tags);
          }
        } catch (error) {
          console.error("Error fetching tags:", error);
        }
      };
      fetchAllTags();
    }
  }, [currentUser]);

  // 4) Format the tags for react-select
  useEffect(() => {
    if (allTags.length > 0) {
      const newTagOptions = allTags.map((tag) => ({
        value: tag.id,
        label: tag.name,
      }));
      setFormattedTags(newTagOptions);
    }
  }, [allTags]);

  // Fetch Existing Relationships
  useEffect(() => {
    if (currentUser && template.id) {
      const fetchRelationships = async () => {
        try {
          const authToken = await currentUser.getIdToken();
          const relationships = await getInterimTaskRelationships(authToken, template.id);
          if (relationships) {
            setExistingRelationships(relationships);
            setInitialRelationships(relationships);
          }
        } catch (error) {
          console.error("Error fetching relationships:", error);
        }
      };
      fetchRelationships();
    }
  }, [currentUser, template.id]);

  // Fetch Relationship Types
  useEffect(() => {
    if (currentUser) {
      const fetchRelationshipTypes = async () => {
        try {
          const authToken = await currentUser.getIdToken();
          const types = await getTaskRelationshipTypes(authToken);
          if (types) {
            setRelationshipTypes(types);
          }
        } catch (error) {
          console.error("Error fetching relationship types:", error);
        }
      };
      fetchRelationshipTypes();
    }
  }, [currentUser]);

  // Fetch All Interim Templates
  useEffect(() => {
    if (currentUser) {
      const fetchAllTemplates = async () => {
        try {
          const authToken = await currentUser.getIdToken();
          const templates = await getInterimTemplates(authToken);
          if (templates) {
            // Exclude the current template from the list
            setAllInterimTemplates(templates.filter(t => t.id !== template.id));
          }
        } catch (error) {
          console.error("Error fetching interim templates:", error);
        }
      };
      fetchAllTemplates();
    }
  }, [currentUser, template.id]);

  // 5) (Optional) If your template already has tags, you can pre-load them:
  //    e.g. if template.tags is an array of Tag objects or just IDs
  //    For now, you can skip or do:
  // useEffect(() => {
  //   if(template.tags) {
  //     const existingTagIds = template.tags.map(t => t.id);
  //     setNewTemplate({...newTemplate, tags: existingTagIds});
  //   }
  // }, [template, newTemplate]);

  const handleDayInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewTemplate({
      ...newTemplate,
      daysFromCreationDueDate: event.target.value,
    });
  };

  // Handle changes for the text fields
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setNewTemplate({ ...newTemplate, [name]: value });
  };

  // Handle requiredFile changes from react-select
  const handleRequiredFileChanges = (
    selectedOptions: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    const requiredFiles = selectedOptions
      ? selectedOptions.map((option) => option.value.toString())
      : [];
    setNewTemplate({ ...newTemplate, requiredFiles });
  };

  // Handle WYSIWYG info changes
  const handleInfoChange = (infoText: string) => {
    setNewTemplate({ ...newTemplate, information: infoText });
  };

  // ===== NEW: handle Tag changes =====
  const handleTagChanges = (
    selectedOptions: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    // We store the array of numeric IDs (or strings if you prefer).
    const selectedTagIds = selectedOptions.map((opt) => Number(opt.value));
    setNewTemplate({ ...newTemplate, tags: selectedTagIds });
  };

  const handleAddRelationship = () => {
    if (newRelationshipTargetId && newRelationshipTypeId) {
      // Check for duplicates
      const alreadyExists = existingRelationships.some(
        (rel) => rel.relationshipTypeId === newRelationshipTypeId && rel.otherTemplateId === newRelationshipTargetId
      );

      if (alreadyExists) {
        alert("This exact relationship already exists.");
        return;
      }

      const targetTemplate = allInterimTemplates.find(t => t.id === newRelationshipTargetId);
      const relationshipType = relationshipTypes.find(rt => rt.id === newRelationshipTypeId);

      if (targetTemplate && relationshipType) {
        const newRelationship: TemplateRelationshipResponse = {
          otherTemplateId: targetTemplate.id,
          otherTemplateTitle: targetTemplate.title,
          relationshipTypeId: relationshipType.id,
          relationshipName: relationshipType.name,
          isSource: true, // Assuming the new relationship is always from the source
          sourceTemplateId: template.id,
          targetTemplateId: targetTemplate.id,
        };
        setExistingRelationships([...existingRelationships, newRelationship]);
        setNewRelationshipTargetId(null);
        setNewRelationshipTypeId(null);
        setIsAddingRelationship(false); // Hide the form
      }
    }
  };

  const handleRemoveRelationship = (targetId: number, typeId: number) => {
    setExistingRelationships(
      existingRelationships.filter(
        (rel) => !(rel.otherTemplateId === targetId && rel.relationshipTypeId === typeId)
      )
    );
  };

  // Submit
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const handleUpdate = async () => {
      if (!currentUser) return;

      try {
        const authToken = await currentUser.getIdToken();

        // 1. Update the template itself
        await updateInterimTemplate(authToken, newTemplate);

        // 2. Determine added and removed relationships
        const initialRels = new Set(initialRelationships.map(r => `${r.otherTemplateId}-${r.relationshipTypeId}`));
        const currentRels = new Set(existingRelationships.map(r => `${r.otherTemplateId}-${r.relationshipTypeId}`));

        const addedRels = existingRelationships.filter(r => !initialRels.has(`${r.otherTemplateId}-${r.relationshipTypeId}`));
        const removedRels = initialRelationships.filter(r => !currentRels.has(`${r.otherTemplateId}-${r.relationshipTypeId}`));

        // 3. Create promises for all API calls
        const createPromises = addedRels.map(rel => {
          const dto: CreateRelationshipDto = {
            targetTemplateId: rel.otherTemplateId,
            relationshipTypeId: rel.relationshipTypeId,
          };
          return createInterimTaskRelationship(authToken, template.id, dto);
        });

        const deletePromises = removedRels.map(rel =>
          deleteInterimTaskRelationship(authToken, template.id, rel.otherTemplateId, rel.relationshipTypeId)
        );

        // 4. Execute all promises
        await Promise.all([...createPromises, ...deletePromises]);

        // 5. On success, navigate away
        setSelectedTemplate(undefined);

      } catch (error) {
        console.error("Failed to update interim template or its relationships:", error);
      }
    };

    handleUpdate();
  };

    const backBtnUrl = (): string => {
        if (currentRoles?.some((role) => role.name === "OWNER" || role.name === "ADMIN")) {
            return "/dashboard/owner/settings";
        } else {
            return "/settings";
        }
    }

  return (
    <>
      <div className="common_card">
        <div className="top top_bar_flx border_tittle">
          <div className="top_bar_tittle">
            <h2><Link to={backBtnUrl()}><img style={{width: '1.5rem'}} src={backarrowbtn} alt="backarrowbtn" /></Link> Edit Interim Template</h2>
          </div>
        </div>
        <form
          onSubmit={handleSubmit}
        >
          {/* Title */}
          <div className="custom_input_flled">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              placeholder="Give your task a title..."
              value={newTemplate.title}
              onChange={handleChange}
              required
            />
          </div>

          {/* Description */}
          <div className="custom_input_flled">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              placeholder="A brief description of what the task entails..."
              value={newTemplate.description}
              onChange={handleChange}
              required
            />
          </div>

          {/* Information (WYSIWYG) */}
          <div className="custom_input_flled">
            <label htmlFor="info">Information</label>
            <TextEditor text={newTemplate.information} setText={handleInfoChange} />
          </div>

          {/* Required Files */}
          <div className="custom_input_flled">
            <label htmlFor="requiredFiles">
              Required Files <span>(Optional)</span>:
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={formattedFileTemplates}
              isMulti
              onChange={handleRequiredFileChanges}
              value={formattedFileTemplates.filter((option) =>
                newTemplate.requiredFiles.includes(option.value.toString())
              )}
            />
          </div>

          {/* Days from creation (due date) */}
          <div className="custom_input_flled">
            <label>Due date from creation</label>
            <input
              type="number"
              min={"1"}
              max={"365"}
              value={newTemplate.daysFromCreationDueDate}
              onChange={handleDayInputChange}
              placeholder={"Days until completion..."}
            />
          </div>

          {/* ===== NEW: Tags ===== */}
          <div className="custom_input_flled">
            <label htmlFor="tags">Tags <span>(Optional)</span>:
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={formattedTags}
              isMulti
              onChange={handleTagChanges}
              // Pre-select tags if newTemplate.tags is not empty
              value={formattedTags.filter((tagOption) =>
                newTemplate.tags?.includes(Number(tagOption.value))
              )}
            />
          </div>

          {/* Task Relationships */}
          <div className="custom_input_flled">
            <label>Task Relationships <span>(Optional)</span>:</label>
            <div className="card">
              <div className="card-body">
                {existingRelationships.length > 0 ? (
                  existingRelationships.map(rel => (
                    <div key={`${rel.otherTemplateId}-${rel.relationshipTypeId}`} className="d-flex justify-content-between align-items-center mb-2">
                      <span>{`This task ${rel.relationshipName.toLowerCase()} '${rel.otherTemplateTitle}'`}</span>
                      <Button variant="danger" size="sm" onClick={() => handleRemoveRelationship(rel.otherTemplateId, rel.relationshipTypeId)}>Remove</Button>
                    </div>
                  ))
                ) : (
                  <div className="text-muted">No relationships defined.</div>
                )}
              </div>
            </div>
            <div className="mt-2">
              {isAddingRelationship ? (
                <div className="d-flex">
                  <div style={{ flex: 1, marginRight: '0.5rem' }}>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={relationshipTypes.map(rt => ({ value: rt.id, label: rt.name }))}
                      onChange={(option) => setNewRelationshipTypeId(option ? Number(option.value) : null)}
                      placeholder="Select relationship type..."
                    />
                  </div>
                  <div style={{ flex: 1, marginLeft: '0.5rem' }}>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={allInterimTemplates.map(t => ({ value: t.id, label: t.title }))}
                      onChange={(option) => setNewRelationshipTargetId(option ? Number(option.value) : null)}
                      placeholder="Select a task..."
                    />
                  </div>
                  <Button variant="primary" onClick={handleAddRelationship} className="ms-2">Add</Button>
                  <Button variant="secondary" onClick={() => setIsAddingRelationship(false)} className="ms-2">Cancel</Button>
                </div>
              ) : (
                <Button variant="secondary" onClick={() => setIsAddingRelationship(true)}>Add Relationship</Button>
              )}
            </div>
          </div>

          <div className="form_btn_flx mby_t_24">
            <Button variant="primary" type="submit">Save Template</Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditTemplateInterimTask;
